import React, { useRef } from "react";
import ReactECharts from "echarts-for-react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { useParams } from "react-router-dom";
import { useGetHWIGraphQuery } from "../operatorDashboardAPISlice";
import { darkBg, gridColor, labelColor } from "../../api/echartsTheme";
import GraphDownloadButton from "../../../Global/other/GraphDownloadButton";

const colorWith    = "#F43F5E"; // rose / pink  — "With LS & PS"
const colorWithout = "#FDB940"; // amber        — "Without LS & PS"

function formatSAST(value) {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    const parts = new Intl.DateTimeFormat("en-ZA", {
      timeZone: "Africa/Johannesburg",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      hour12: false,
    }).formatToParts(d);
    const p = Object.fromEntries(parts.map(({ type, value: v }) => [type, v]));
    return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second} SAST`;
  } catch {
    return String(value);
  }
}

function HWIChart({ title, subtitle, data, color, legendLabel, tooltipName, meterId, csvLabel, yMin, yMax }) {
  const chartRef = useRef(null);

  const handleDownloadCsv = () => {
    if (!data?.length) return;
    const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    const dateTag = new Date().toISOString().slice(0, 10);
    const header = `Timestamp (SAST),${csvLabel ?? tooltipName}`;
    const rows = data.map((item) => `${formatSAST(item.interval)},${item.value ?? ""}`);
    const csv = "\uFEFF" + [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hwi-${slug}-${dateTag}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadPng = () => {
    const instance = chartRef.current?.getEchartsInstance();
    if (!instance) return;
    const url = instance.getDataURL({ type: "png", pixelRatio: 2, backgroundColor: "#0d1117" });
    const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    const dateTag = new Date().toISOString().slice(0, 10);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hwi-${slug}-${dateTag}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const seriesData = (data || []).map((item) => [
    new Date(item.interval).getTime(),
    item.value ?? null,
  ]);

  const option = {
    backgroundColor: "transparent",
    animation: false,
    grid: { top: 20, right: 24, bottom: 72, left: 56 },
    dataZoom: [
      { type: "inside", xAxisIndex: 0, filterMode: "filter" },
      {
        type: "slider",
        xAxisIndex: 0,
        height: 18,
        bottom: 32,
        backgroundColor: "rgba(255,255,255,0.03)",
        borderColor: "rgba(255,255,255,0.07)",
        fillerColor: color + "33",
        handleStyle: { color, borderColor: color },
        moveHandleStyle: { color },
        emphasis: { handleStyle: { color } },
        textStyle: { color: "transparent" },
        brushSelect: false,
        showDetail: false,
      },
    ],
    tooltip: {
      trigger: "axis",
      backgroundColor: "#1C232C",
      borderColor: "rgba(255,255,255,0.12)",
      textStyle: { color: "#e0e6f0", fontSize: 12 },
      axisPointer: {
        type: "line",
        lineStyle: { color: "rgba(255,255,255,0.25)", type: "dashed" },
      },
      formatter: (params) => {
        if (!params.length) return "";
        const d = new Date(params[0].value[0]);
        const dateStr =
          `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}` +
          ` · ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
        const rows = params
          .filter((p) => p.value?.[1] != null)
          .map(
            (p) =>
              `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};margin-right:6px"></span>` +
              `${tooltipName}&nbsp;&nbsp;<b>${Number(p.value[1]).toFixed(2)}</b>`
          )
          .join("<br/>");
        return `<div style="font-size:11px;color:rgba(255,255,255,0.45);margin-bottom:6px">${dateStr}</div>${rows}`;
      },
    },
    legend: {
      bottom: 4,
      textStyle: { color: labelColor, fontSize: 11 },
      itemWidth: 10,
      itemHeight: 10,
      data: [{ name: legendLabel, icon: "circle" }],
    },
    xAxis: {
      type: "time",
      axisLine: { lineStyle: { color: gridColor } },
      axisTick: { show: false },
      axisLabel: {
        color: labelColor,
        fontSize: 10,
        formatter: (v) => {
          const d = new Date(v);
          return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
        },
      },
      splitLine: { show: false },
    },
    yAxis: {
      type: "value",
      ...(yMin != null && yMax != null ? { min: yMin, max: yMax, scale: true } : {}),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: labelColor, fontSize: 10, formatter: (v) => v.toFixed(2) },
      splitLine: { lineStyle: { color: gridColor, type: "dashed" } },
    },
    series: [
      {
        name: legendLabel,
        type: "line",
        smooth: true,
        symbol: "none",
        lineStyle: { color, width: 2 },
        itemStyle: { color },
        connectNulls: false,
        data: seriesData,
      },
    ],
  };

  return (
    <Box
      sx={{
        backgroundColor: darkBg,
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      {/* Card header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          px: "20px",
          pt: "18px",
          pb: "4px",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#fff" }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: "0.78rem", color: labelColor, mt: "2px" }}>
            {subtitle}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0, ml: "16px" }}>

          <GraphDownloadButton onDownloadCsv={handleDownloadCsv} onDownloadPng={handleDownloadPng} />
        </Box>
      </Box>

      <ReactECharts ref={chartRef} option={option} style={{ height: 380 }} notMerge />
    </Box>
  );
}

export default function Graph({ startDate }) {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { meter_id } = useParams();

  const request = {
    meter_id,
    timezone: userTimezone,
    ...(startDate && { start_date: startDate.toISOString() }),
  };

  const { data: HotWaterIndex, isLoading, isError } = useGetHWIGraphQuery(request, {
    pollingInterval: startDate ? 0 : 60000,
  });

  if (isLoading) {
    return (
      <Grid item sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}>
        <CircularProgress size={20} sx={{ color: colorWith }} />
        <Typography sx={{ color: labelColor }}>Loading HWI data…</Typography>
      </Grid>
    );
  }

  if (isError) {
    return (
      <Grid item sx={{ p: 2 }}>
        <Typography sx={{ color: "#e05050" }}>Error retrieving HWI data</Typography>
      </Grid>
    );
  }

  const withData = (HotWaterIndex?.data || []).map((item) => ({
    interval: item.interval,
    value: item.hot_water_index,
  }));

  const withoutData = (HotWaterIndex?.data || []).map((item) => ({
    interval: item.interval,
    value: item.hwi_simulated,
  }));

  // Shared y-axis range for the two HWI charts (NEU-744)
  const hwiValues = [...withData, ...withoutData]
    .map((d) => d.value)
    .filter((v) => v != null && !isNaN(v));
  let hwiYMin = 0;
  let hwiYMax = 100;
  if (hwiValues.length > 0) {
    const dataMin = Math.min(...hwiValues);
    const dataMax = Math.max(...hwiValues);
    hwiYMin = Math.max(0, Math.floor(dataMin / 5) * 5 - 5);
    hwiYMax = Math.min(100, Math.ceil(dataMax / 5) * 5 + 5);
    if (hwiYMin === hwiYMax) { hwiYMin = Math.max(0, hwiYMin - 5); hwiYMax = Math.min(100, hwiYMax + 5); }
  }

  const powerRatingData = (HotWaterIndex?.data || []).map((item) => ({
    interval: item.interval,
    value: item.power_rating,
  }));

  return (
    <Grid container spacing={2} direction="column">
      <Grid item xs={12}>
        <HWIChart
          title="With load shifting & peak shaving"
          subtitle="Hot Water Index · last 2 hours · 60 s polling"
          data={withData}
          color={colorWith}
          legendLabel="HWI (hot_water_index)"
          tooltipName="HWI"
          csvLabel="Hot Water Index"
          meterId={meter_id}
          yMin={hwiYMin}
          yMax={hwiYMax}
        />
      </Grid>
      <Grid item xs={12}>
        <HWIChart
          title="Without load shifting & peak shaving"
          subtitle="Simulated HWI · counterfactual without intervention"
          data={withoutData}
          color={colorWithout}
          legendLabel="HWI simulated (hwi_simulated)"
          tooltipName="HWI sim."
          csvLabel="HWI Simulated"
          yMin={hwiYMin}
          yMax={hwiYMax}
        />
      </Grid>
      <Grid item xs={12}>
        <HWIChart
          title="Tjommie load"
          subtitle="Geyser demand (kW) · per minute"
          data={powerRatingData}
          color={colorWithout}
          legendLabel="Power rating (kW)"
          tooltipName="Load (kW)"
          csvLabel="Power Rating (kW)"
        />
      </Grid>
    </Grid>
  );
}
